import { Charge } from '@/types/charges'
import {
  getChargeLimitWithUnit,
  getChargeRemainingTime,
  getIsCharging,
} from './utils'
import { ChargeForecast, VehicleChargeSchedule } from '@/types'
import { Text } from '@/components'
import { selectedVehicle } from '@/selectors/vehicles'
import { useAppSelector } from '@/hooks'
import { DeviceDemandResponseEvent } from '@/types/deviceDemandResponseEvent'
import { timeAgo } from '@/utils/date'
import dayjs from 'dayjs'
import duration from 'dayjs/plugin/duration'
dayjs.extend(duration)

type Props = {
  latestCharge: Charge | null
  chargeLimit?: number
  chargeForecast: ChargeForecast | undefined
  chargeSchedule: VehicleChargeSchedule | undefined
  milesPerBatteryPercent: number | null
  activeDREvent?: DeviceDemandResponseEvent | null
}

export default function ChargeForecastSubtitle({
  chargeForecast,
  chargeLimit,
  chargeSchedule,
  latestCharge,
  milesPerBatteryPercent,
  activeDREvent,
}: Props) {
  const mode = getIsCharging(chargeForecast) ? 'Charging' : 'NotCharging'

  const kW = Math.round(latestCharge?.kw ?? 0)
  const amp =
    latestCharge?.average_amperage ??
    Math.round(
      (kW * 1000) / (latestCharge?.charger_type === '240v' ? 240 : 120),
    )
  const chargeRemainingTime = getChargeRemainingTime(chargeForecast)

  const vehicle = useAppSelector((state) =>
    selectedVehicle(state.vehicles.vehicles),
  )

  const drEventEndingTime = activeDREvent
    ? (() => {
        const now = dayjs()
        const endTime = dayjs(activeDREvent.end_datetime)
        const duration = dayjs.duration(endTime.diff(now))
        const hours = Math.floor(duration.asHours())
        const minutes = duration.minutes()

        if (hours > 0 && minutes > 0) {
          return `DR event ending in ${hours}hr ${minutes}m`
        } else if (minutes > 0) {
          return `DR event ending in ${minutes}m`
        }
        return null
      })()
    : null

  const powerDescriptor = kW && amp ? `${amp} A • ${kW} kW` : null
  const remainingTimeDescriptor = chargeRemainingTime
    ? `${chargeRemainingTime} remain`
    : null

  const chargingSubtitle =
    !powerDescriptor && !remainingTimeDescriptor
      ? undefined
      : `${[powerDescriptor, remainingTimeDescriptor]
          .filter(Boolean)
          .join(' • ')}`

  const chargeLimitPercentage = getChargeLimitWithUnit({
    chargeLimit: chargeLimit ?? null,
    chargeLimitUnit: chargeSchedule?.battery_target_unit ?? 'percent',
    milesPerBatteryPercent: milesPerBatteryPercent ?? null,
    outputUnit: 'percent',
  })

  const chargeLimitSubtitle = chargeLimitPercentage
    ? `Charge limit: ${chargeLimitPercentage}%`
    : undefined

  return (
    <>
      {mode === 'Charging' && chargingSubtitle && (
        <Text className="font-medium" variant="body2">
          {chargingSubtitle}
        </Text>
      )}
      {drEventEndingTime && (
        <Text className="font-medium" variant="body2">
          {drEventEndingTime}
        </Text>
      )}
      {chargeLimitSubtitle && (
        <Text className="font-medium" variant="body2">
          {chargeLimitSubtitle}
        </Text>
      )}
      {vehicle?.last_ping && !activeDREvent && (
        <Text className="font-medium" variant="body2">
          {`Last updated: ${timeAgo(vehicle?.last_ping?.toString())}`}
        </Text>
      )}
    </>
  )
}
